/* global theme */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      /* ========= STATE ========= */
      this.cachedResults = {};
      this.swiper = null;

      /* ========= DOM ========= */
      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('#predictive-search');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');

      /* ========= EVENTS ========= */
      this.input.addEventListener(
        'input',
        this.debounce(() => this.onChange(), 300)
      );

      this.input.addEventListener('focus', () => this.onFocus());
      this.resetBtn.addEventListener('click', () => this.resetSearch());

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.addEventListener('keyup', this.handleKeyup.bind(this));
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      document.addEventListener('click', this.handleOutsideClick);
    }

    /* ========= HELPERS ========= */

handleOutsideClick(e) {
  if (!this.hasAttribute('open')) return;

  const isOverlay = e.target.closest('.js-search-overlay');

  // If click inside component BUT not overlay → ignore
  if (this.contains(e.target) && !isOverlay) return;

  this.close();
}



    debounce(fn, delay) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    getQuery() {
      return this.input.value.trim();
    }

    /* ========= INPUT ========= */

    onChange() {
      const term = this.getQuery();
      this.toggleResetButton();

      if (!term) {
        this.showCategories();
        return;
      }

      this.getResults(term);
    }

    onFocus() {
      if (!this.getQuery()) {
        this.showCategories();
      } else if (this.hasAttribute('results')) {
        this.open();
      } else {
        this.getResults(this.getQuery());
      }
    }

    resetSearch() {
      this.input.value = '';
      this.toggleResetButton();
      this.removeAttribute('results');
      this.showCategories();
    }

    /* ========= REQUEST LOGIC (FROM FIRST SCRIPT) ========= */

    async getResults(searchTerm) {
  this.setLiveRegionLoadingState();

  const searchFields =
    'title,product_type,variants.title,vendor,variants.sku,tag';

  const params =
    `q=${encodeURIComponent(searchTerm)}` +
    `&resources[type]=query,product,collection,page,article` +
    `&resources[limit]=10` +
    `&resources[limit_scope]=each` +
    `&resources[options][fields]=${encodeURIComponent(searchFields)}` +
    `&section_id=predictive-search`;

  const cacheKey = params.toLowerCase();

  if (this.cachedResults[cacheKey]) {
    this.renderResults(this.cachedResults[cacheKey]);
    return;
  }

  try {
    const res = await fetch(`/search/suggest?${params}`);
    if (!res.ok) throw new Error(res.status);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('#shopify-section-predictive-search');
    if (!section) return;

    const markup = section.innerHTML;
    this.cachedResults[cacheKey] = markup;
    this.renderResults(markup);
  } catch (err) {
    console.error('Predictive search failed', err);
    this.close();
  }
}


    /* ========= RENDER ========= */

    renderResults(markup) {
       const temp = document.createElement('div');
  temp.innerHTML = markup;

  const hasProducts = temp.querySelector('.predictive-products li');
  const hasCollections = temp.querySelector('[data-panel="collections"] li');
  const hasPages = temp.querySelector('[data-panel="pages"] li');
  const hasQueries = temp.querySelector('.query-item');

  const isEmpty = !hasProducts && !hasCollections && !hasPages && !hasQueries;

  if (isEmpty) {
    this.renderEmptyState();
    return;
  }

      this.resultsContainer.innerHTML = markup;
      this.setAttribute('results', 'true');
      this.open();
      this.setDefaultActiveTab();
    }

    /* ========= TABS ========= */

    setDefaultActiveTab() {
      const container = this.resultsContainer.querySelector('.predictive-left');
      if (!container) return;

      const tabs = container.querySelectorAll('.tab');
      const panels = container.querySelectorAll('.tab-panel');

      tabs.forEach(t => t.classList.remove('is-active-tab'));
      panels.forEach(p => p.classList.remove('is-active-tab'));

      let activeTab = container.querySelector('.tab[data-tab="products"]');
      if (!activeTab) activeTab = tabs[0];

      const target = activeTab.dataset.tab;
      activeTab.classList.add('is-active-tab');
      container
        .querySelector(`[data-panel="${target}"]`)
        ?.classList.add('is-active-tab');
    }

renderEmptyState() {
  const query = this.getQuery();

  this.resultsContainer.innerHTML = `
    <div class="predictive-search-no-results">
      <p>No results found for "<strong>${query}</strong>".</p>
    </div>
  `;

  this.setAttribute('results', 'true');
  this.open();
}

    /* ========= KEYBOARD NAV ========= */

    handleKeydown(e) {
      if (!this.hasAttribute('open')) return;

      const items = this.querySelectorAll(
        '.tab-panel.is-active-tab [role="option"], .predictive-search__item'
      );
      if (!items.length) return;

      let current = this.querySelector('[aria-selected="true"]');
      let index = Array.from(items).indexOf(current);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          index = index + 1 >= items.length ? 0 : index + 1;
          this.updateSelection(items, index);
          break;

        case 'ArrowUp':
          e.preventDefault();
          index = index - 1 < 0 ? items.length - 1 : index - 1;
          this.updateSelection(items, index);
          break;

        case 'Enter':
          if (current) {
            const link = current.querySelector('a');
            if (link) link.click();
          }
          break;

        case 'Escape':
          this.close();
          break;
      }
    }

    handleKeyup(e) {
      // When input is cleared by backspace, show categories (onclick container) instead of closing.
      // Closing here caused a race: close() ran immediately, then debounced onChange re-opened
      // with showCategories(), which broke the slider and left stale predictive results visible.
      if (!this.getQuery() && this.hasAttribute('open')) {
        this.showCategories();
      }
    }

    updateSelection(items, index) {
      items.forEach(i => i.setAttribute('aria-selected', 'false'));
      const active = items[index];
      active.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', active.id || '');
      this.setLiveRegionText(active.textContent);
    }

    /* ========= LIVE REGION ========= */

    setLiveRegionLoadingState() {
      this.setLiveRegionText('Loading search results');
    }

    setLiveRegionText(text) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text;
      this.statusEl.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 800);
    }

    /* ========= CATEGORIES ========= */

    showCategories() {
      const template = document.getElementById('categories-template');
      if (!template) return;

      this.resultsContainer.innerHTML = template.innerHTML;
      this.setAttribute('results', 'true');
      this.open();
      this.setDefaultActiveTab();

      const swiperEl = this.resultsContainer.querySelector('.custom-predictive-swiper');
      if (swiperEl) {
        if (this.swiper) this.swiper.destroy(true, true);
        this.swiper = new Swiper(swiperEl, {
          spaceBetween: 30,
          freeMode: true,
          mousewheel: { forceToAxis: true },
          breakpoints: {
            340: { slidesPerView: 2.5, spaceBetween: 20 },
            768: { slidesPerView: 2.5, spaceBetween: 30 },
            1024: { slidesPerView: 5, spaceBetween: 30 },
          },
        });
      }
    }

    /* ========= OPEN / CLOSE ========= */

    open() {
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.overlay?.classList.add('is-visible');
      document.body.classList.add('overlay-predictive-search');
      document.documentElement.classList.add('overlay-predictive-search');
    }

    close() {
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.overlay?.classList.remove('is-visible');
      const anyOpen = document.querySelector('predictive-search[open]');
      if (!anyOpen) {
        document.body.classList.remove('overlay-predictive-search');
        document.documentElement.classList.remove('overlay-predictive-search');
      }
    }

    toggleResetButton() {
      this.resetBtn?.classList.toggle(
        'is-visible',
        this.getQuery().length > 0
      );
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}

/* ========= TAB CLICK ========= */
document.addEventListener('click', (e) => {
  const tab = e.target.closest('.tab');
  if (!tab) return;

  e.preventDefault();

  const container = tab.closest('.predictive-left');
  if (!container) return;

  const target = tab.dataset.tab;

  container.querySelectorAll('.tab').forEach(t =>
    t.classList.remove('is-active-tab')
  );
  container.querySelectorAll('.tab-panel').forEach(p =>
    p.classList.remove('is-active-tab')
  );

  tab.classList.add('is-active-tab');
  container
    .querySelector(`[data-panel="${target}"]`)
    ?.classList.add('is-active-tab');
});
